const classNames = {
  dialog: "lightbox__dialog",
  closeButton: "lightbox__close-button",
  dialogContent: "lightbox__dialog-content",
}

export class Lightbox {
  private dialog: HTMLDialogElement;
  private closeButton: HTMLButtonElement;
  private dialogContent: HTMLDivElement;
  private resizeListener: () => void;
  private overrides: { [key: string]: string; }
  private preventClosingOnBodyClick: boolean;

  constructor() {
    this.preventClosingOnBodyClick = false;
    this.createDialog();
    this.addEventListeners();

    window.Lightbox = this;
  }

  private createDialog() {
    const dialog = document.createElement("dialog");
    const dialogContent = document.createElement("div");
    const closeButton = document.createElement("button");

    dialog.classList.add(classNames.dialog);
    dialogContent.classList.add(classNames.dialogContent);
    closeButton.classList.add(classNames.closeButton);
    closeButton.setAttribute("aria-label", "Close");

    document.body.appendChild(dialog);
    dialog.appendChild(closeButton);
    dialog.appendChild(dialogContent);

    this.dialog = dialog;
    this.closeButton = closeButton;
    this.dialogContent = dialogContent;
  }

  private addEventListeners() {
    this.closeButton.addEventListener("click", () => this.closeModal());
    this.dialog.addEventListener("click", (event) => {
      if (event.target === this.dialog) {
        !this.preventClosingOnBodyClick && this.closeModal();
      }
    });
  }

  public openModal(content: HTMLElement, overrides?: { [key: string]: string; }, preventClosingOnBodyClick: boolean = false) {
    this.preventClosingOnBodyClick = preventClosingOnBodyClick;
    this.dialogContent.innerHTML = "";
    this.dialogContent.appendChild(content);
    this.overrides = overrides;

    this.setDimensions(content);
    this.setResizeListener();

    this.dialog.showModal();
  }

  public addContentToModal(content: HTMLElement) {
    this.dialogContent.appendChild(content);
  }

  public setDimensions(content: HTMLElement) {
    const width = window.innerWidth;
    const isLightboxImage = content instanceof HTMLImageElement && content.classList.contains("lightbox__image");

    if (isLightboxImage) {
      // Use auto sizing so the element box matches the rendered image dimensions.
      // This ensures a sibling lightbox-title left-aligns with the visual image edge
      // rather than the wider container edge (which object-fit:scale-down would otherwise create).
      const maxW = window.innerWidth <= 992 ? width : Math.round(width * 0.9);
      content.style.width = "auto";
      content.style.height = "auto";
      content.style.maxWidth = `${Math.min(maxW, 1150)}px`;
      content.style.maxHeight = "1150px";
      content.style.display = "block";
    } else {
      if (window.innerWidth <= 992) {
        content.style.width = `${width}px`;
      } else {
        content.style.width = `${width * 0.9}px`;
      }
      content.style.maxWidth = "1150px";
      content.style.maxHeight = "1150px";
      content.style.alignContent = "center";
    }

    if (this.overrides) {
      for (const [key, value] of Object.entries(this.overrides)) {
        content.style[key] = value;
      }
    }
  }

  public closeModal() {
    this.removeResizeListener();
    this.dialogContent.innerHTML = "";

    this.dialog.close();
    this.overrides = null;
  }

  public setResizeListener() {
    this.resizeListener = () => {
      if (this.dialog.open) {
        const content = this.dialogContent.firstElementChild as HTMLElement;
        if (content) {
          this.setDimensions(content);
        }
      }
    }

    window.addEventListener("resize", this.resizeListener);
  }

  public removeResizeListener() {
    if (this.resizeListener) {
      window.removeEventListener("resize", this.resizeListener);
      this.resizeListener = null;
    }
  }
}

if (document.readyState !== "loading") {
  new Lightbox()
} else {
  document.addEventListener("DOMContentLoaded", () => new Lightbox());
}